import React, { useEffect, useRef, useState } from 'react';
import { FiMoreVertical, FiEye, FiEdit, FiPrinter, FiArchive, FiTrash2 } from "react-icons/fi";
import ReactDOM from 'react-dom';
import ViewDetailPrescription from '../Popup/ViewDetailPrescription';
import EditPrescription from '../Popup/EditPrescription';
import prescriptionService from '../services/prescriptionService'; // Import the service

const ActionTableButton = ({ 
  item, 
  refreshData,
  viewModalTarget = "viewPrescriptionDetailModal" 
}) => {
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);
  
  // States
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [showModal, setShowModal] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  
  // Function to update a specific item locally
  const updateItemLocally = (id, updatedFields) => {
    setPrescriptions(prevPrescriptions => 
      prevPrescriptions.map(prescription => 
        (prescription.id === id || prescription._id === id) 
          ? { ...prescription, ...updatedFields } 
          : prescription
      )
    );
  };

  // Determine if the prescription is active or not
  const isActive = item.isActive !== undefined ? item.isActive : true;

  // Calculate dropdown position
  const calculatePosition = () => {
    if (!buttonRef.current) return { top: 0, left: 0 };
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    return {
      top: buttonRect.bottom + scrollTop,
      left: buttonRect.right - 150 + scrollLeft, // 150px is the approximate width of the dropdown
    };
  };
  
  // Handle action button click
  const toggleDropdown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Calculate and set position before showing
    const position = calculatePosition();
    setDropdownPosition(position);
    setDropdownVisible(!dropdownVisible);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownVisible && 
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target)
      ) {
        setDropdownVisible(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownVisible]);
  
  // Effect to handle scroll lock when modal is open
  useEffect(() => {
    if (showModal) {
      // Prevent scroll on body
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '15px'; // Compensate for scrollbar
    } else {
      // Restore scroll
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    
    return () => {
      // Cleanup when unmounting
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showModal]);
  
  // Action functions
  const handleViewDetails = (e) => {
    e.stopPropagation();
    console.log("View details for prescription:", item.id || item._id);
    setSelectedPrescription(item);
    setIsEditMode(false);
    setShowModal(true);
    setDropdownVisible(false);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setSelectedPrescription(null);
    setIsEditMode(false);
  };
  
  const handleEdit = (e) => {
    e.stopPropagation();
    console.log('Edit prescription with ID:', item.id || item._id);
    // Prepare prescription object with a signal to indicate edit mode
    const prescriptionWithEditMode = { ...item, initialEditMode: true };
    setSelectedPrescription(prescriptionWithEditMode);
    setIsEditMode(true);
    setShowModal(true);
    setDropdownVisible(false);
  };
  
  const handlePrint = (e) => {
    e.stopPropagation();
    console.log('Print prescription with ID:', item.id || item._id);
    
    // Create a new window for printing
    const printWindow = window.open('', '', 'width=800,height=600');
    
    // Create HTML content for printing with all available data
    printWindow.document.write(`
      <html>
        <head>
          <title>Prescription Details</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px;
            }
            .print-header { 
              text-align: center; 
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            h1 { 
              margin-bottom: 5px;
            }
            .prescription-id {
              font-size: 14px;
              color: #666;
              margin-bottom: 20px;
            }
            .section {
              margin-bottom: 25px;
            }
            .section-title {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 10px;
              border-bottom: 1px solid #ddd;
              padding-bottom: 5px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px; 
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold; 
            }
            .patient-doctor-info {
              display: flex;
              justify-content: space-between;
            }
            .patient-info, .doctor-info {
              width: 48%;
            }
            .footer {
              margin-top: 40px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
              font-size: 12px;
              text-align: center;
              color: #666;
            }
            @media print {
              .page-break {
                page-break-before: always;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>Medical Prescription</h1>
            <div class="prescription-id">Prescription ID: ${item.id || item._id}</div>
            <div>Date: ${item.datePrescribed || new Date().toLocaleDateString()}</div>
          </div>
          
          <div class="patient-doctor-info">
            <div class="patient-info section">
              <div class="section-title">Patient Information</div>
              <p><strong>Name:</strong> ${item.patient?.name || item.patientName || "N/A"}</p>
              <p><strong>ID:</strong> ${item.patient?._id || item.patientId || "N/A"}</p>
            </div>
            
            <div class="doctor-info section">
              <div class="section-title">Doctor Information</div>
              <p><strong>Name:</strong> ${item.doctor?.name || item.doctorName || "N/A"}</p>
              <p><strong>ID:</strong> ${item.doctor?._id || item.doctorId || "N/A"}</p>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Clinical Information</div>
            <p><strong>Symptoms:</strong> ${item.symptoms || "None recorded"}</p>
            <p><strong>Diseases:</strong> ${item.diseases || "None recorded"}</p>
            <p><strong>Status:</strong> ${item.isActive ? "Active" : "Inactive"}</p>
          </div>
          
          <div class="section">
            <div class="section-title">Prescribed Medicines</div>
            <table>
              <thead>
                <tr>
                  <th>Medicine</th>
                  <th>Dose</th>
                  <th>Frequency</th>
                  <th>Duration</th>
                  <th>Instructions</th>
                  <th>Refills</th>
                </tr>
              </thead>
              <tbody>
                ${(item.prescribeMedicine && Array.isArray(item.prescribeMedicine)) ? 
                  item.prescribeMedicine.map(med => `
                    <tr>
                      <td>${med.medicine || ""}</td>
                      <td>${med.dose || ""}</td>
                      <td>${med.frequency || ""}</td>
                      <td>${med.duration || ""}</td>
                      <td>${med.instruction || ""}</td>
                      <td>${med.refills || 0}</td>
                    </tr>
                  `).join('') : 
                  `<tr>
                    <td>${item.medicineId || ""}</td>
                    <td>${item.dosage || ""}</td>
                    <td>${item.frequency || ""}</td>
                    <td>${item.duration || ""}</td>
                    <td>${item.instructions || ""}</td>
                    <td>${item.refills || 0}</td>
                  </tr>`
                }
              </tbody>
            </table>
          </div>
          
          ${(item.prescribeTests && Array.isArray(item.prescribeTests) && item.prescribeTests.length > 0) || item.tests ? `
            <div class="section">
              <div class="section-title">Prescribed Tests</div>
              <table>
                <thead>
                  <tr>
                    <th>Test Name</th>
                  </tr>
                </thead>
                <tbody>
                  ${Array.isArray(item.prescribeTests) ? 
                    item.prescribeTests.map(test => `
                      <tr>
                        <td>${test.name || test || ""}</td>
                      </tr>
                    `).join('') : 
                    item.tests ? item.tests.split(',').map(test => `
                      <tr>
                        <td>${test.trim()}</td>
                      </tr>
                    `).join('') : ''
                  }
                </tbody>
              </table>
            </div>
          ` : ''}
          
          <div class="footer">
            <p>This prescription was generated on ${new Date().toLocaleString()}</p>
            <p>Please consult healthcare provider for any questions or concerns.</p>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              }
            }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    setDropdownVisible(false);
  };
  
  // Improved version of handleArchive for ActionTableButton.js
  const handleArchive = async (e) => {
    e.stopPropagation();
    
    try {
      // Get current status and prescription ID
      const isActive = item.isActive !== undefined ? item.isActive : true;
      const newStatus = !isActive;
      const prescriptionId = item.id || item._id;
      
      // Define action name for messages
      const actionName = isActive ? 'archive' : 'unarchive';
      
      console.log(`Attempting to ${actionName} prescription with ID:`, prescriptionId);
      
      // Show confirmation to user
      if (window.confirm(`Are you sure you want to ${actionName} this prescription?`)) {
        // Call service to update status
        const response = await prescriptionService.updatePrescriptionActiveStatus(prescriptionId, newStatus);
        
        // Verify response
        if (response && response.status) {
          console.log(`Prescription ${actionName}d successfully:`, prescriptionId);
          
          // Show message to user
          alert(`The prescription has been ${actionName}d successfully.`);
          
          // Update only the element in local state
          // Assuming you have access to the function to update state:
          if (updateItemLocally) {
            updateItemLocally(prescriptionId, { isActive: newStatus });
            window.location.reload();
          } else {
            console.warn('No function provided to update locally, using refreshData or reloading');
            // Use refreshData as fallback
            if (refreshData) {
              refreshData();
            } else {
              window.location.reload();
            }
          }
        } else {
          throw new Error(response?.message || `Could not ${actionName} the prescription`);
        }
      }
    } catch (error) {
      console.error(`Error ${isActive ? 'archiving' : 'unarchiving'} the prescription:`, error);
      alert(`Error: Could not ${isActive ? 'archive' : 'unarchive'} the prescription. ${error.message}`);
    }
    
    // Close dropdown regardless of the result
    setDropdownVisible(false);
  };
  
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete this prescription?`)) {
      try {
        const prescriptionId = item.id || item._id;
        console.log('Delete prescription with ID:', prescriptionId);
        
        // Call the delete API
        await prescriptionService.deletePrescription(prescriptionId);
        
        alert('Prescription deleted successfully');
        
        // If there's a refreshData function, call it with the deleted item
        if (refreshData) {
          refreshData(item);
          window.location.reload(); // Pasar el elemento eliminado
        } else {
          // If no refreshData function is provided, refresh the page
          window.location.reload();
        }
      } catch (error) {
        console.error('Error deleting prescription:', error);
        alert('Failed to delete prescription. Please try again.');
      }
      
      setDropdownVisible(false);
    }
  };
  
  const handleUpdateSuccess = async (updatedPrescription) => {
    try {
      console.log('Preparing to update prescription:', updatedPrescription);
      
      // Format the data according to what backend expects
      const formattedData = {
        _id: updatedPrescription._id,
        doctor: updatedPrescription.doctor?._id || updatedPrescription.doctor,
        patient: updatedPrescription.patient?._id || updatedPrescription.patient,
        symptoms: updatedPrescription.symptoms,
        diseases: Array.isArray(updatedPrescription.diseases) 
          ? updatedPrescription.diseases.map(d => typeof d === 'object' ? d._id : d)
          : [],
        prescribeMedicine: Array.isArray(updatedPrescription.prescribeMedicine) 
          ? updatedPrescription.prescribeMedicine.map(med => ({
              medicine: med.medicine,
              dose: med.dose,
              frequency: med.frequency,
              duration: med.duration,
              instruction: med.instruction,
              refills: med.refills
            }))
          : [],
        prescribeTests: Array.isArray(updatedPrescription.prescribeTests)
          ? updatedPrescription.prescribeTests.map(test => typeof test === 'object' ? test.name : test)
          : [],
        isActive: updatedPrescription.isActive
      };
      
      console.log('Sending formatted data to API:', formattedData);
      
      // Use the service to update the prescription
      const prescriptionId = updatedPrescription._id || updatedPrescription.id;
      const response = await prescriptionService.updatePrescription(prescriptionId, formattedData);
      
      console.log('Update response:', response);
      
      if (response && response.status) {
        alert('Prescription updated successfully');
        if (refreshData) refreshData();
        closeModal();
      } else {
        throw new Error(response?.message || 'Failed to update prescription');
      }
    } catch (error) {
      console.error('Error updating prescription:', error);
      alert(`Update failed: ${error.message}`);
    }
  };
  
  const handleCancel = () => {
    // Handle cancellation
    console.log('Edit cancelled');
    // Close the modal
    closeModal();
  };
  
  // Dropdown component that will render in portal
  const DropdownMenu = () => {
    if (!dropdownVisible) return null;
    
    return ReactDOM.createPortal(
      <div 
        ref={dropdownRef}
        className="dropdown-menu show"
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          zIndex: 9999,
          minWidth: '150px',
          backgroundColor: '#fff',
          boxShadow: '0 2px 10px rgba(0,0,0,.2)',
          border: '1px solid rgba(0,0,0,.15)',
          borderRadius: '4px',
          padding: '0.5rem 0'
        }}
      >
        <button 
          className="dropdown-item d-flex align-items-center" 
          type="button"
          onClick={handleViewDetails}
        >
          <FiEye className="me-2" /> View Details
        </button>
        <button 
          className="dropdown-item d-flex align-items-center" 
          type="button"
          onClick={handleEdit}
        >
          <FiEdit className="me-2" /> Edit
        </button>
        <button 
          className="dropdown-item d-flex align-items-center" 
          type="button"
          onClick={handlePrint}
        >
          <FiPrinter className="me-2" /> Print
        </button>
        <button 
          className="dropdown-item d-flex align-items-center" 
          type="button"
          onClick={handleArchive}
        >
          <FiArchive className="me-2" /> {isActive ? "Archive" : "Unarchive"}
        </button>
        <button 
          className="dropdown-item d-flex align-items-center text-danger" 
          type="button"
          onClick={handleDelete}
        >
          <FiTrash2 className="me-2" /> Delete
        </button>
      </div>,
      document.body
    );
  };
  
  // Modal component for viewing details
  const DetailModal = () => {
    if (!showModal) return null;
    
    // Using portal to render the modal outside the normal DOM flow
    return ReactDOM.createPortal(
      <div className="modal-backdrop-custom">
        <div 
          ref={modalRef}
          className="modal-custom" 
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div 
            className="modal-dialog modal-lg" 
            style={{
              maxWidth: '800px',
              width: '100%',
              margin: '10px',
              backgroundColor: '#fff',
              borderRadius: '5px',
              boxShadow: '0 5px 15px rgba(0,0,0,.5)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh' 
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
              overflow: 'hidden'
            }}>
              <div className="modal-header" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                borderBottom: '1px solid #dee2e6'
              }}>
                <h5 className="modal-title">
                  {isEditMode ? 'Edit Prescription' : 'Prescription Details'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={closeModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{
                padding: '1rem',
                overflow: 'auto'
              }}>
                {selectedPrescription && !isEditMode && ( 
                  <ViewDetailPrescription 
                    prescription={selectedPrescription} 
                  />
                )}
                {selectedPrescription && isEditMode && (
                  <EditPrescription 
                    prescription={selectedPrescription} 
                    onUpdateSuccess={handleUpdateSuccess}
                    onCancel={handleCancel}
                  />
                )}
              </div>
              <div className="modal-footer" style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '1rem',
                borderTop: '1px solid #dee2e6'
              }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };
  
  return (
    <>
      <div onClick={e => e.stopPropagation()}>
        <button 
          ref={buttonRef}
          className="btn btn-sm" 
          type="button" 
          onClick={toggleDropdown}
          aria-expanded={dropdownVisible}
        >
          <FiMoreVertical />
        </button>
      </div>
      
      {/* Render dropdown menu through portal */}
      <DropdownMenu />
      
      {/* Render modal through portal */}
      <DetailModal />
    </>
  );
};

export default ActionTableButton;